import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Icon from 'components/Icon'
import classNames from 'classnames'
import {
  fetchAdAccounts,
  LNB_ACCOUNT_SIZE,
  setPage,
  setSearch,
} from 'features/lnb/lnbSlice'
import PropTypes from 'prop-types'
import useOutsideClick from 'utils/hooks/useOutsideClick'
import Loading from '../../components/loading/Loading'
import NoneAnchor from '../../components/NoneAnchor'

const OnOffIcon = ({ userConfig, outOfBalance, status = [] }) =>
  userConfig === 'ON' &&
  outOfBalance !== 'Y' &&
  status.includes('STOPPED') === false &&
  status.includes('NOT_CONNECTED_BIZ_WALLET') === false ? (
    <span className="ico_on">운영중</span>
  ) : (
    <span className="ico_off">운영중지</span>
  )
OnOffIcon.propTypes = {
  userConfig: PropTypes.string.isRequired,
  outOfBalance: PropTypes.string.isRequired,
  status: PropTypes.arrayOf(PropTypes.string),
}

const AdAccountLayer = ({ close }) => {
  const dispatch = useDispatch()
  const { accountId } = useParams()
  const adAccountId = Number(accountId)
  const { search, adAccounts, isLoading, page, totalElements } = useSelector(
    state => state.lnb
  )
  const lastPage = useMemo(
    () => Math.max(Math.ceil(totalElements / LNB_ACCOUNT_SIZE) - 1, 0),
    [totalElements]
  )
  const selectedIndex = adAccounts.findIndex(({ id }) => id === adAccountId)
  const scrollRef = useCallback(
    node => {
      if (node) {
        node.scrollTop = selectedIndex === -1 ? 0 : selectedIndex * 44
      }
    },
    [selectedIndex]
  )
  const [input, setInput] = useState(search)
  const [focused, setFocused] = useState(false)

  const handleSubmit = useCallback(
    event => {
      event.preventDefault()
      if (!isLoading) {
        dispatch(setSearch(input))
      }
    },
    [dispatch, input, isLoading]
  )

  return (
    <div className="layer_account">
      <div className="search_account">
        <div
          className={classNames('box_inptxt search_inp', {
            on: focused,
            in_active: isLoading,
          })}>
          <span className="inner_inp">
            <form onSubmit={handleSubmit} autoComplete="off">
              <button type="submit" className="btn_search">
                <Icon className="ico_gnb" name="search">
                  검색
                </Icon>
              </button>
              <input
                type="text"
                className="inp_txt"
                placeholder="광고계정 이름을 입력하세요."
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                value={input}
                disabled={isLoading}
                onChange={({ target: { value } }) => setInput(value)}
              />
              {input !== '' && (
                <button
                  type="button"
                  className="btn_del"
                  onClick={() => setInput('')}
                  style={{ ...(input !== '' && { display: 'block' }) }}>
                  <Icon name="del" />
                </button>
              )}
            </form>
          </span>
        </div>
      </div>
      <div className="fake_scroll" ref={scrollRef}>
        <ul className="list_account">
          {isLoading ? (
            <div className="txt_nodata">
              <Loading />
            </div>
          ) : search !== '' && adAccounts.length === 0 ? (
            <p className="txt_nodata">검색 결과가 없습니다.</p>
          ) : (
            adAccounts.map(
              ({ id, name, master, userConfig, outOfBalance, status }) => (
                <li className={classNames({ on: id === adAccountId })} key={id}>
                  <Link
                    to={`/${id}/dashboard`}
                    className="link_option"
                    onClick={close}>
                    {master && (
                      <div className="opt_state">
                        <span className="ico_badge ico_master">마스터</span>
                      </div>
                    )}
                    <span className="txt_option">
                      <OnOffIcon
                        userConfig={userConfig}
                        outOfBalance={outOfBalance}
                        status={status}
                      />
                      {name}
                      <span className="txt_num">{id}</span>
                    </span>
                  </Link>
                </li>
              )
            )
          )}
        </ul>
      </div>
      <div className="opt_tool">
        <div className="paging_wrap2">
          <span className="inner_paging">
            <strong className="num_current">
              <span className="screen_out">현재 광고계정 리스트</span>
              {page + 1}
            </strong>
            / <span className="screen_out">총 광고계정 리스트 수</span>
            {lastPage + 1}
          </span>
          {page === 0 || isLoading ? (
            <span className="num_paging num_prev">
              <span className="ico_comm ico_prev">이전</span>
            </span>
          ) : (
            <NoneAnchor
              className="num_paging num_prev"
              onClick={() => dispatch(setPage(page - 1))}>
              <span className="ico_comm ico_prev">이전</span>
            </NoneAnchor>
          )}
          {page === lastPage || isLoading ? (
            <span className="num_paging num_next">
              <span className="ico_comm ico_next">다음</span>
            </span>
          ) : (
            <NoneAnchor
              className="num_paging num_next"
              onClick={() => dispatch(setPage(page + 1))}>
              <span className="ico_comm ico_next">다음</span>
            </NoneAnchor>
          )}
        </div>
        <Link to="/adaccount/all" className="link_all">
          전체보기
        </Link>
        <Link to="/adaccount/all/create" className="link_create">
          + 새 광고계정 만들기
        </Link>
      </div>
    </div>
  )
}
AdAccountLayer.propTypes = {
  close: PropTypes.func,
}

const AdAccountFinder = forwardRef((props, ref) => {
  const dispatch = useDispatch()
  const { isExpanded, isOver, search, page } = useSelector(state => state.lnb)
  const {
    current: { name: adAccountName },
  } = useSelector(state => state.account)
  const [layerVisible, setLayerVisible] = useState(false)
  const close = () => setLayerVisible(false)
  const clickRef = useOutsideClick(close, ref)

  useEffect(() => {
    if (isExpanded === false && isOver === false) {
      setLayerVisible(false)
    }
  }, [isExpanded, isOver])

  useEffect(() => {
    dispatch(fetchAdAccounts())
  }, [dispatch, search, page])

  return (
    <div
      className={classNames('user_info', { on: layerVisible })}
      ref={clickRef}>
      <strong
        className="tit_account"
        onClick={() => {
          dispatch(setSearch(''))
          setLayerVisible(!layerVisible)
        }}>
        {adAccountName}
        <span className="ico_gnb ico_arrow">더보기</span>
      </strong>
      {layerVisible && <AdAccountLayer close={close} />}
    </div>
  )
})
AdAccountFinder.displayName = 'AdAccountFinder'
export default AdAccountFinder
